import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Building2, Users, LayoutDashboard, Settings, Euro, LogOut, Mail, MapPin, CreditCard, Calendar, Building, ShoppingBag, Copy, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import Logo from '../components/Logo';

const AdminConcierges = () => {
  const navigate = useNavigate();
  const [concierges, setConcierges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, concierge: null });
  const [deleting, setDeleting] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('IBAN copié !');
  };

  useEffect(() => {
    fetchConcierges();
  }, []);

  const fetchConcierges = async () => {
    try {
      const response = await api.get('/admin/concierges');
      setConcierges(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des concierges');
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConcierge = async () => {
    if (!deleteDialog.concierge) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/concierges/${deleteDialog.concierge.id}`);
      toast.success(`Concierge "${deleteDialog.concierge.nom}" supprimé`);
      setDeleteDialog({ open: false, concierge: null });
      fetchConcierges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 p-6 space-y-8">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
          <Logo variant="full" size="h-12" />
        </div>
        <nav className="space-y-2">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <LayoutDashboard className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Dashboard
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin/options')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <Settings className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Options Globales
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin/revenue')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <Euro className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Paiements Concierges
          </Button>
          <Button variant="ghost" className="w-full justify-start text-rose-600 bg-rose-50 hover:bg-rose-100">
            <Users className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Liste Concierges
          </Button>
        </nav>
        
        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            onClick={() => { localStorage.clear(); navigate('/auth'); }}
            className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Déconnexion
          </Button>
        </div>
      </aside>

      <div className="ml-64">
        <header className="bg-white border-b border-slate-100 px-8 py-6">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Liste des Concierges
            </h1>
            <p className="text-base text-slate-600 mt-1">
              {concierges.length} concierge{concierges.length > 1 ? 's' : ''} inscrit{concierges.length > 1 ? 's' : ''}
            </p>
          </div>
        </header>

        <main className="p-8">
          {concierges.length === 0 ? (
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Aucun concierge inscrit
              </h3>
              <p className="text-sm text-slate-500">
                Les concierges apparaîtront ici une fois inscrits
              </p>
            </Card>
          ) : (
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-700">Concierge</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-700">Entreprise</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-700">SIRET</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-700">IBAN</th>
                      <th className="text-center py-4 px-4 text-sm font-medium text-slate-700">Logements</th>
                      <th className="text-center py-4 px-4 text-sm font-medium text-slate-700">Ventes</th>
                      <th className="text-right py-4 px-4 text-sm font-medium text-slate-700">CA Total</th>
                      <th className="text-left py-4 px-4 text-sm font-medium text-slate-700">Inscription</th>
                      <th className="text-center py-4 px-4 text-sm font-medium text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {concierges.map((concierge, index) => (
                      <tr 
                        key={concierge.id}
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                        }`}
                      >
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{concierge.nom}</p>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {concierge.email}
                            </p>
                            {concierge.adresse && (
                              <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {concierge.adresse}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-slate-600">
                          {concierge.entreprise || '-'}
                        </td>
                        <td className="py-4 px-4">
                          {concierge.siret ? (
                            <code className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                              {concierge.siret}
                            </code>
                          ) : '-'}
                        </td>
                        <td className="py-4 px-4">
                          {concierge.iban ? (
                            <div className="flex items-center gap-2">
                              <code className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                                {concierge.iban}
                              </code>
                              <button
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(concierge.iban); }}
                                className="p-1 hover:bg-slate-200 rounded transition-colors"
                                title="Copier l'IBAN"
                              >
                                <Copy className="w-3 h-3 text-slate-500" />
                              </button>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-slate-700">
                            <Building className="w-4 h-4 text-slate-400" />
                            {concierge.nb_properties}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-slate-700">
                            <ShoppingBag className="w-4 h-4 text-slate-400" />
                            {concierge.nb_transactions}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-bold text-rose-600 tabular-nums">
                            {(concierge.total_ventes || 0).toFixed(2)} €
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {concierge.created_at ? new Date(concierge.created_at).toLocaleDateString('fr-FR') : '-'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, concierge })}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-concierge-${concierge.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-slate-300 bg-rose-50">
                    <tr>
                      <td colSpan="5" className="py-4 px-4 text-right font-bold text-slate-900">
                        TOTAUX
                      </td>
                      <td className="py-4 px-4 text-center font-bold text-slate-700">
                        {concierges.reduce((sum, c) => sum + (c.nb_transactions || 0), 0)}
                      </td>
                      <td className="py-4 px-4 text-right font-bold text-rose-600 tabular-nums text-lg">
                        {concierges.reduce((sum, c) => sum + (c.total_ventes || 0), 0).toFixed(2)} €
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}
        </main>
      </div>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Supprimer ce concierge ?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Vous êtes sur le point de supprimer <strong>{deleteDialog.concierge?.nom}</strong> ({deleteDialog.concierge?.email}).
              <br /><br />
              Cette action supprimera également :
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>{deleteDialog.concierge?.nb_properties || 0} logement(s)</li>
                <li>Toutes les options associées</li>
                <li>Tous les prestataires</li>
              </ul>
              <br />
              <strong className="text-red-600">Cette action est irréversible.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, concierge: null })}
              disabled={deleting}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConcierge}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminConcierges;
